import styled, { css } from 'styled-components';
import { Container } from '../layout/Container';

export const AuthPage = styled.div`
   min-height: 100vh;
   width: 100%;
   background: ${({ theme }) => theme.colors.appBackground};
   display: flex;
   align-items: center;
`;

export const AuthContainer = styled(Container)`
   padding-top: 24px;
   padding-bottom: 24px;
`;

export const AuthGrid = styled.div`
   display: grid;
   gap: 16px;
   grid-template-columns: minmax(0, 1fr) 420px;
   align-items: stretch;
   min-height: 560px;

   @media (max-width: 900px) {
      grid-template-columns: 1fr;
      min-height: auto;
   }
`;

export const AuthVisual = styled.div`
   position: relative;
   overflow: hidden;

   min-height: 560px;
   height: 100%;

   border-radius: 16px;
   border: 1px solid ${({ theme }) => theme.colors.borderOnAppBackground};
   padding: 18px;
   color: ${({ theme }) => theme.colors.textOnAppBackground};

   background: ${({ $image }) =>
      $image
         ? `url(${$image}) center/cover no-repeat`
         : `linear-gradient(135deg, rgba(64, 152, 239, 0.22), rgba(255, 255, 255, 0.06))`};

   /* Overlay pra contraste do texto */
   &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
         135deg,
         rgba(0, 0, 0, 0.55),
         rgba(0, 0, 0, 0.15)
      );
      pointer-events: none;
   }

   > * {
      position: relative;
      z-index: 1;
   }

   @media (max-width: 900px) {
      display: none;
   }
`;

export const AuthCard = styled.div`
   border-radius: 16px;
   background: ${({ theme }) => theme.colors.surface};
   border: 1px solid ${({ theme }) => theme.colors.border};
   box-shadow: ${({ theme }) => theme.shadows.sm};
   padding: 18px;

   min-height: 560px;
   height: 100%;

   display: grid;
   grid-template-rows: 2fr 6fr 2fr; /* 20% / 60% / 20% */

   @media (min-width: 900px) {
      padding: 22px;
   }

   /* Em telas baixas ou mobile, melhor deixar o conteúdo fluir naturalmente */
   @media (max-width: 900px) {
      min-height: auto;
      grid-template-rows: auto 1fr auto;
   }

   /* ── Variante fluida ─────────────────────────────────────────────────────
      O card default assume TRÊS filhos (topo / corpo / rodapé) e reserva
      2fr:6fr:2fr sobre 560px de altura mínima. Quem renderiza só topo+corpo
      ganha um vão morto embaixo da logo, e o card ainda força a coluna vizinha
      a esticar. A prop $fluid desliga as duas premissas: altura pelo conteúdo
      e uma trilha só. A prop $maxWidth centraliza o card quando ele é o único
      elemento da tela (sem AuthGrid/AuthVisual ao lado).

      Sem crase nesta caixa de comentário: ela vive dentro do template literal
      do styled-components, e uma crase aqui fecha a string. */
   ${({ $fluid }) =>
      $fluid &&
      css`
         min-height: auto;
         height: auto;
         grid-template-rows: auto;
         align-content: start;
      `}

   ${({ $maxWidth }) =>
      $maxWidth &&
      css`
         width: 100%;
         max-width: ${typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth};
         margin: 0 auto;
      `}
`;

export const AuthCardTop = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: center;
`;

export const AuthCardBody = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center; /* form no “meio” */
   min-height: 0;
`;

export const AuthCardFooter = styled.div`
   display: flex;
   align-items: flex-end;
   justify-content: center;
   padding-top: 8px;
`;

export const AuthFootnote = styled.div`
   width: 100%;
   display: flex;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;

   font-size: 12px;
   color: ${({ theme }) => theme.colors.textMuted};

   a {
      color: ${({ theme }) => theme.colors.primary};
      text-decoration: none;
      font-weight: 600;
   }

   a:hover {
      text-decoration: underline;
   }
`;

export const AuthTitle = styled.h1`
   margin: 0;
   font-size: 20px;
   color: ${({ theme }) => theme.colors.textPrimary};
`;

export const AuthSubtitle = styled.p`
   margin: 6px 0 0;
   font-size: 14px;
   color: ${({ theme }) => theme.colors.textMuted};
`;

export const AuthForm = styled.form`
   margin-top: 16px;
   display: grid;
   gap: 12px;
`;

export const AuthActions = styled.div`
   display: flex;
   justify-content: space-between;
   margin-top: 6px;
`;

export const InputGroup = styled.div`
   display: flex;
   column-gap:24px;
`;

export const AuthBrand = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   margin-bottom: 12px;
`;

export const AuthLogo = styled.img`
   height: 44px;
   width: auto;
   object-fit: contain;

   @media (min-width: 900px) {
      height: 52px;
   }
`;
